/**
 * Advanced Cache Service for Witcher Gaming Platform
 * Provides localStorage-based caching with different expiration strategies
 */
export class CacheService {
  constructor() {
    this.CACHE_PREFIX = 'witcher_'
    this.CONSTANTS = {
      MILLISECONDS_PER_HOUR: 60 * 60 * 1000,
      MILLISECONDS_PER_DAY: 24 * 60 * 60 * 1000
    }
  }

  /**
   * Calculate expiration time based on strategy
   * @param {string} strategy - 'hours' or 'end-of-day'
   * @param {number} hours - Number of hours for 'hours' strategy
   * @returns {number} Expiration timestamp
   */
  getExpirationTime(strategy, hours = 0) {
    const now = new Date()

    if (strategy === 'hours') {
      return now.getTime() + hours * this.CONSTANTS.MILLISECONDS_PER_HOUR
    }

    if (strategy === 'end-of-day') {
      const tomorrow = new Date(now)
      tomorrow.setDate(tomorrow.getDate() + 1)
      tomorrow.setHours(0, 0, 0, 0) // Set to 00:00:00
      return tomorrow.getTime()
    }

    // Default fallback for unknown strategies
    return now.getTime() + this.CONSTANTS.MILLISECONDS_PER_DAY
  }

  /**
   * Set cache item with expiration strategy
   * @param {string} key - Cache key
   * @param {*} data - Data to cache
   * @param {string} strategy - Expiration strategy
   * @param {number} hours - Hours for 'hours' strategy
   */
  set(key, data, strategy, hours = 0) {
    try {
      const expiry = this.getExpirationTime(strategy, hours)
      const cacheItem = {
        data,
        expiry,
        strategy,
        cachedAt: new Date().getTime(),
        hours: hours || 0
      }
      localStorage.setItem(
        `${this.CACHE_PREFIX}${key}`,
        JSON.stringify(cacheItem)
      )
    } catch (error) {
      console.warn(`Failed to cache item ${key}:`, error)
    }
  }

  /**
   * Get cached data
   * @param {string} key - Cache key
   * @returns {*} Cached data or null if expired/missing
   */
  get(key) {
    try {
      const item = localStorage.getItem(`${this.CACHE_PREFIX}${key}`)
      if (!item) return null

      const { data, expiry } = JSON.parse(item)
      if (new Date().getTime() > expiry) {
        this.remove(key)
        return null
      }
      return data
    } catch (error) {
      console.warn(`Failed to get cached item ${key}:`, error)
      return null
    }
  }

  /**
   * Remove cached item
   * @param {string} key - Cache key
   */
  remove(key) {
    try {
      localStorage.removeItem(`${this.CACHE_PREFIX}${key}`)
    } catch (error) {
      console.warn(`Failed to remove cached item ${key}:`, error)
    }
  }

  /**
   * Get countdown information for cached item
   * @param {string} key - Cache key
   * @returns {Object|null} Countdown information
   */
  getCountdown(key) {
    try {
      const item = localStorage.getItem(`${this.CACHE_PREFIX}${key}`)
      if (!item) return null

      const { expiry, strategy, hours } = JSON.parse(item)
      const now = new Date().getTime()

      if (now > expiry) return null

      const remainingMs = expiry - now
      const remainingHours = Math.floor(
        remainingMs / this.CONSTANTS.MILLISECONDS_PER_HOUR
      )
      const remainingMinutes = Math.floor(
        (remainingMs % this.CONSTANTS.MILLISECONDS_PER_HOUR) / (1000 * 60)
      )
      const remainingSeconds = Math.floor((remainingMs % (1000 * 60)) / 1000)

      return {
        strategy,
        hours: hours || 0,
        remaining: {
          ms: remainingMs,
          hours: remainingHours,
          minutes: remainingMinutes,
          seconds: remainingSeconds,
          formatted: CacheService.formatCountdown(
            remainingHours,
            remainingMinutes,
            remainingSeconds
          )
        },
        expiresAt: new Date(expiry),
        isExpired: remainingMs <= 0
      }
    } catch (error) {
      console.warn(`Failed to get countdown for ${key}:`, error)
      return null
    }
  }

  /**
   * Format countdown time as HH:MM:SS
   * @param {number} hours
   * @param {number} minutes
   * @param {number} seconds
   * @returns {string} Formatted countdown
   */
  static formatCountdown(hours, minutes, seconds) {
    return `${hours}:${minutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }

  /**
   * Check if cached item is valid (not expired)
   * @param {string} key - Cache key
   * @returns {boolean} True if valid
   */
  isValid(key) {
    const countdown = this.getCountdown(key)
    return countdown && !countdown.isExpired
  }

  /**
   * Clear all cache items with the prefix
   */
  clearAll() {
    try {
      const keys = Object.keys(localStorage)
      keys.forEach(key => {
        if (key.startsWith(this.CACHE_PREFIX)) {
          localStorage.removeItem(key)
        }
      })
    } catch (error) {
      console.warn('Failed to clear cache:', error)
    }
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics
   */
  getStats() {
    try {
      const keys = Object.keys(localStorage)
      const cacheKeys = keys.filter(key => key.startsWith(this.CACHE_PREFIX))

      let totalSize = 0
      let validItems = 0
      let expiredItems = 0

      cacheKeys.forEach(key => {
        const item = localStorage.getItem(key)
        if (item) {
          totalSize += item.length
          const shortKey = key.replace(this.CACHE_PREFIX, '')
          if (this.isValid(shortKey)) {
            validItems += 1
          } else {
            expiredItems += 1
          }
        }
      })

      return {
        totalItems: cacheKeys.length,
        validItems,
        expiredItems,
        totalSize,
        totalSizeKB: Math.round((totalSize / 1024) * 100) / 100
      }
    } catch (error) {
      console.warn('Failed to get cache stats:', error)
      return null
    }
  }
}

// Export singleton instance
export const cacheService = new CacheService()
export default cacheService
